import { createPortal } from "react-dom";
import "./SessionDetailsModal.css";
import { generateDocAction } from "./generateUrl";
import { usePublicSession } from "./usePublicSession";
import DownloadButton from "./DownloadButton";
import { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const SessionDetailsModal = ({ isOpen, onClose, clientUid, computerId }) => {
  const { publicSession, loading, error } = usePublicSession(
    clientUid,
    computerId
  );

  const [openDocId, setOpenDocId] = useState(null);

  const toggleDocOptions = (id) => {
    setOpenDocId((prev) => (prev === id ? null : id));
  };

  if (!isOpen || !publicSession) return null;

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(parseFloat(timestamp) * 1000);
    return date.toLocaleString("fr-FR");
  };

  const formatPrice = (price) => {
    return `${price}€`;
  };

  const generateUrl = async (docId) => {
    const res = await generateDocAction({
      clientUid: clientUid,
      computerId: computerId,
      docId: docId,
      status: "pending_upload",
      url: "",
    });

    console.log("res ODC", res);
  };

  const downloadFile = async (docId, url) => {
    const res = await generateDocAction({
      clientUid: clientUid,
      computerId: computerId,
      docId: docId,
      status: "downloaded",
      url: url,
    });

    console.log("res ODC", res);
  };

  const renderDocuments = (documents, title, visitorDeposit) => {
    if (!documents || Object.keys(documents).length === 0) {
      return <p>Aucun {title.toLowerCase()}</p>;
    }

    return Object.entries(documents)
      .sort(([, a], [, b]) => {
        const dateA = parseFloat(a.date) || 0;
        const dateB = parseFloat(b.date) || 0;
        return dateB - dateA; // tri décroissant (du plus récent au plus ancien)
      })
      .map(([id, doc]) => {
        return (
          <div key={id} className="document-item">
            <div className="document-header">
              <h4>{doc.document_name || "Document sans nom"}</h4>

              {doc.download_status === "none" && !visitorDeposit && (
                <button className="doc-btn" onClick={() => generateUrl(id)}>
                  Génerer un lien
                </button>
              )}

              {doc.download_status === "pending_upload" && (
                <span className="doc-loader">
                  <span className="doc-spinner"></span>
                  Generating...
                </span>
              )}

              {doc.download_status === "error" && (
                <div className="doc-error-container">
                  <span className="doc-error">Erreur survenue</span>
                  <button className="doc-btn" onClick={() => generateUrl(id)}>
                    Ressayer
                  </button>
                </div>
              )}

              {doc.download_status === "uploaded" && !visitorDeposit && (
                <DownloadButton
                  url={doc.url}
                  id={id}
                  onClick={downloadFile}
                  label="Télécharger"
                />
              )}

              {visitorDeposit && doc.download_status !== "downloaded" && (
                <DownloadButton
                  url={doc.url}
                  id={id}
                  onClick={downloadFile}
                  label="Télécharger"
                  visitorDeposit={visitorDeposit}
                />
              )}

              {doc.download_status === "downloaded" && (
                <DownloadButton
                  url={doc.url}
                  id={id}
                  onClick={downloadFile}
                  label="Télécharger à nouveau"
                  visitorDeposit={visitorDeposit}
                />
              )}
            </div>
            <div key={id} className="document-details">
              <div className="document-details-header">
                <p>
                  <strong>Date:</strong> {formatDate(doc.date)}
                </p>
                {doc.price && (
                  <p>
                    <strong>Prix:</strong> {formatPrice(doc.price)}
                  </p>
                )}
                {doc.printed_pages_total && (
                  <p>
                    <strong>Pages imprimées:</strong> {doc.printed_pages_total}
                  </p>
                )}
                {doc.pages_scanned && (
                  <p>
                    <strong>Pages scannées:</strong> {doc.pages_scanned}
                  </p>
                )}
                {doc.type && (
                  <p>
                    <strong>Type:</strong> {doc.type}
                  </p>
                )}
              </div>
              {doc.options && (
                <div className="document-options">
                  <p>
                    <strong>Options d'impression:</strong>
                  </p>
                  <button onClick={() => toggleDocOptions(id)}>
                    {openDocId === id
                      ? "Masquer les détails"
                      : "Voir les détails"}
                    {openDocId === id ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
              )}
              {doc.options && openDocId === id && (
                <div className="options">
                  <ul>
                    {doc.options.monochrome !== undefined && (
                      <li>
                        Couleur:{" "}
                        {doc.options.monochrome ? "Monochrome" : "Couleur"}
                      </li>
                    )}
                    {doc.options.orientation && (
                      <li>Orientation: {doc.options.orientation}</li>
                    )}
                    {doc.options.printer && (
                      <li>Imprimante: {doc.options.printer}</li>
                    )}
                    {doc.options.page_count && (
                      <li>Nombre de pages: {doc.options.page_count}</li>
                    )}
                    {doc.options.paperSize && (
                      <li>Taille papier: {doc.options.paperSize}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      });
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Détails de la session</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="session-summary">
            <h3>Résumé de la session</h3>
            <div className="summary-grid">
              <div className="summary-row">
                <div className="summary-item">
                  <strong>Date de début:</strong>{" "}
                  {formatDate(publicSession.start_date)}
                </div>
                <div className="summary-item">
                  <strong>Prix total:</strong>{" "}
                  {formatPrice(publicSession.session_price_total || 0)}
                </div>
              </div>
              <div className="summary-row">
                <div className="summary-item">
                  <strong>Impressions noir/blanc:</strong>{" "}
                  {publicSession.pages_black_white_total || 0}
                </div>
                <div className="summary-item">
                  <strong>Impressions couleur:</strong>{" "}
                  {publicSession.pages_color_total || 0}
                </div>
                <div className="summary-item">
                  <strong>Pages scannées:</strong>{" "}
                  {publicSession.pages_scan_total || 0}
                </div>
              </div>
            </div>
          </div>

          <div className="documents-section">
            <h3>Historique des impressions</h3>
            <div className="documents-list">
              {renderDocuments(
                publicSession.documents_printed,
                "fichier imprimé",
                false
              )}
            </div>
          </div>

          <div className="documents-section">
            <h3>Historique des fichiers scannés</h3>
            <div className="documents-list">
              {renderDocuments(
                publicSession.documents_scanned,
                "fichier scanné",
                false
              )}
            </div>
          </div>
          <div className="documents-section">
            <h3>Fichier importés</h3>
            <div className="documents-list">
              {renderDocuments(
                publicSession.documents_imported,
                "fichier importé",
                false
              )}
            </div>
          </div>
          <div className="documents-section">
            <h3>Fichiers envoyés</h3>
            <div className="documents-list">
              {renderDocuments(
                publicSession.visitor_deposit,
                "fichier envoyé",
                true
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SessionDetailsModal;
